import { authService } from '../../services/auth';
import { adminApi, type AdminUserDto } from '../../services/admin.api';
import { billingApi, type BillingPlanDto } from '../../services/billing.api';
import { getEuCountryChoices } from '../../services/eu-countries';
import { onLocaleChange, t } from '../../services/i18n';
import { openAdminForceCancelAllPaidSubscriptionsDialog, openAdminForceCancelSubscriptionsDialog, openAdminPlatformResetDialog, openAdminUserDetails } from './admin-dialogs';
import { normalizeBooleanFilter, parseBooleanFilter, type AdminBooleanFilter, esc } from './admin-shared';
import './rc-admin-users-view';
import { RcAdminUsersView } from './rc-admin-users-view';

const ADMIN_EVENTS = ['apply-filters', 'reset-filters', 'open-details', 'open-platform-reset', 'open-force-cancel-selected', 'open-force-cancel-all-paid', 'page', 'toggle-advertising', 'save-allowed-countries', 'toggle-google-login', 'toggle-microsoft-login', 'toggle-new-user-access', 'toggle-new-subscription-access', 'toggle-user-selection', 'toggle-select-all-users'] as const;

export class RcAdminUsers extends HTMLElement {
  private users: AdminUserDto[] = [];
  private allowedCountryCodes: string[] = [];
  private advertisingEnabled = true;
  private googleLoginEnabled = true;
  private microsoftLoginEnabled = true;
  private newUserAccessEnabled = true;
  private newSubscriptionAccessEnabled = true;
  private isSavingAdvertising = false;
  private isSavingAllowedCountries = false;
  private isSavingGoogleLogin = false;
  private isSavingMicrosoftLogin = false;
  private isSavingNewUserAccess = false;
  private isSavingNewSubscriptionAccess = false;
  private advertisingError = '';
  private allowedCountriesError = '';
  private googleLoginError = '';
  private microsoftLoginError = '';
  private newUserAccessError = '';
  private newSubscriptionAccessError = '';
  private isLoading = false;
  private error = '';
  private totalCount = 0;
  private page = 1;
  private pageSize = 25;
  private totalPages = 0;
  private billingPlans: BillingPlanDto[] = [];
  private selectedUserIds = new Set<string>();
  private filters = {
    name: '',
    email: '',
    id: '',
    business: '' as AdminBooleanFilter,
    onboarding: '' as AdminBooleanFilter,
  };
  private disposeLocaleChange?: () => void;

  connectedCallback(): void {
    ADMIN_EVENTS.forEach((eventName) => this.addEventListener(`admin-users-${eventName}`, this.handleAdminEvent as EventListener));
    this.render();
    this.disposeLocaleChange = onLocaleChange(() => this.render());
    if (authService.isAuthenticated) {
      void this.loadUsers();
    }
  }

  disconnectedCallback(): void {
    this.disposeLocaleChange?.();
    ADMIN_EVENTS.forEach((eventName) => this.removeEventListener(`admin-users-${eventName}`, this.handleAdminEvent as EventListener));
  }

  private async loadUsers(): Promise<void> {
    this.isLoading = true;
    this.error = '';
    this.render();
    try {
      const result = await adminApi.getUsers({
        name: this.filters.name || undefined,
        email: this.filters.email || undefined,
        id: this.filters.id || undefined,
        business: parseBooleanFilter(this.filters.business),
        onboarding: parseBooleanFilter(this.filters.onboarding),
        page: this.page,
        pageSize: this.pageSize,
      });
      this.users = result.items;
      const visibleIds = new Set(this.users.map((user) => user.id));
      this.selectedUserIds = new Set([...this.selectedUserIds].filter((id) => visibleIds.has(id)));
      this.allowedCountryCodes = result.allowedCountryCodes?.length
        ? [...result.allowedCountryCodes]
        : getEuCountryChoices().map((country) => country.code) as string[];
      this.advertisingEnabled = result.advertisingEnabled ?? true;
      this.googleLoginEnabled = result.googleLoginEnabled ?? true;
      this.microsoftLoginEnabled = result.microsoftLoginEnabled ?? true;
      this.newUserAccessEnabled = result.newUserAccessEnabled;
      this.newSubscriptionAccessEnabled = result.newSubscriptionAccessEnabled ?? true;
      this.advertisingError = '';
      this.allowedCountriesError = '';
      this.googleLoginError = '';
      this.microsoftLoginError = '';
      this.newUserAccessError = '';
      this.newSubscriptionAccessError = '';
      this.totalCount = result.totalCount;
      this.page = result.page;
      this.pageSize = result.pageSize;
      this.totalPages = result.totalPages;
    } catch (error: any) {
      this.error = error?.message || t('admin.loadError');
    } finally {
      this.isLoading = false;
      this.render();
    }
  }

  private render(): void {
    if (!authService.isAuthenticated || !authService.isAdmin) {
      this.innerHTML = `<div class="empty-state">${t('admin.accessDenied')}</div>`;
      return;
    }
    if (this.isLoading) {
      this.innerHTML = `<div class="empty-state">${t('common.loading')}</div>`;
      return;
    }
    if (this.error) {
      this.innerHTML = `<div class="empty-state">${esc(this.error)}</div>`;
      return;
    }

    this.innerHTML = '<rc-admin-users-view></rc-admin-users-view>';
    const view = this.querySelector('rc-admin-users-view') as RcAdminUsersView | null;
    if (!view) return;
    view.users = this.users;
    view.allowedCountryCodes = this.allowedCountryCodes;
    view.advertisingEnabled = this.advertisingEnabled;
    view.googleLoginEnabled = this.googleLoginEnabled;
    view.microsoftLoginEnabled = this.microsoftLoginEnabled;
    view.newUserAccessEnabled = this.newUserAccessEnabled;
    view.newSubscriptionAccessEnabled = this.newSubscriptionAccessEnabled;
    view.isSavingAdvertising = this.isSavingAdvertising;
    view.isSavingAllowedCountries = this.isSavingAllowedCountries;
    view.isSavingGoogleLogin = this.isSavingGoogleLogin;
    view.isSavingMicrosoftLogin = this.isSavingMicrosoftLogin;
    view.isSavingNewUserAccess = this.isSavingNewUserAccess;
    view.isSavingNewSubscriptionAccess = this.isSavingNewSubscriptionAccess;
    view.advertisingError = this.advertisingError;
    view.allowedCountriesError = this.allowedCountriesError;
    view.googleLoginError = this.googleLoginError;
    view.microsoftLoginError = this.microsoftLoginError;
    view.newUserAccessError = this.newUserAccessError;
    view.newSubscriptionAccessError = this.newSubscriptionAccessError;
    view.totalCount = this.totalCount;
    view.page = this.page;
    view.pageSize = this.pageSize;
    view.totalPages = this.totalPages;
    view.filters = this.filters;
    view.selectedUserIds = [...this.selectedUserIds];
    view.render();
  }

  private async ensureBillingPlansLoaded(): Promise<void> {
    if (this.billingPlans.length) {
      return;
    }

    const overview = await billingApi.getOverview();
    this.billingPlans = overview.plans;
  }

  private handleAdminEvent = async (event: Event): Promise<void> => {
    const detail = (event as CustomEvent<any>).detail ?? {};
    switch ((event as CustomEvent).type) {
      case 'admin-users-apply-filters': {
        const form = detail.form as HTMLFormElement | undefined;
        if (!form) return;
        const fd = new FormData(form);
        this.filters = {
          name: String(fd.get('name') || '').trim(),
          email: String(fd.get('email') || '').trim(),
          id: String(fd.get('id') || '').trim(),
          business: normalizeBooleanFilter(fd.get('business')),
          onboarding: normalizeBooleanFilter(fd.get('onboarding')),
        };
        this.page = 1;
        await this.loadUsers();
        return;
      }
      case 'admin-users-reset-filters':
        this.filters = { name: '', email: '', id: '', business: '', onboarding: '' };
        this.page = 1;
        await this.loadUsers();
        return;
      case 'admin-users-open-details':
        if (typeof detail.index === 'number' && this.users[detail.index]) {
          let plansError = '';

          try {
            await this.ensureBillingPlansLoaded();
          } catch (error: any) {
            plansError = error?.message || t('admin.subscription.loadPlansError');
          }

          openAdminUserDetails({
            user: this.users[detail.index],
            plans: this.billingPlans,
            plansError,
            onSaved: async () => {
              await this.loadUsers();
            },
            onDeleted: async () => {
              await this.loadUsers();
            },
          });
        }
        return;
      case 'admin-users-open-platform-reset':
        openAdminPlatformResetDialog(async () => {
          await this.loadUsers();
        });
        return;
      case 'admin-users-open-force-cancel-selected': {
        const selectedUsers = this.users.filter((user) => this.selectedUserIds.has(user.id) && user.hasManageableSubscription);
        if (!selectedUsers.length) {
          return;
        }

        openAdminForceCancelSubscriptionsDialog(selectedUsers, async () => {
          this.selectedUserIds.clear();
          await this.loadUsers();
        });
        return;
      }
      case 'admin-users-open-force-cancel-all-paid':
        openAdminForceCancelAllPaidSubscriptionsDialog(async () => {
          this.selectedUserIds.clear();
          await this.loadUsers();
        });
        return;
      case 'admin-users-page':
        if (!Number.isNaN(detail.page) && detail.page >= 1 && detail.page !== this.page) {
          this.page = detail.page;
          await this.loadUsers();
        }
        return;
      case 'admin-users-toggle-advertising': {
        if (this.isSavingAdvertising || typeof detail.isEnabled !== 'boolean') {
          return;
        }

        const previousValue = this.advertisingEnabled;
        this.isSavingAdvertising = true;
        this.advertisingError = '';
        this.advertisingEnabled = detail.isEnabled;
        this.render();

        try {
          await adminApi.setAdvertising({ isEnabled: detail.isEnabled });
        } catch (error: any) {
          this.advertisingEnabled = previousValue;
          this.advertisingError = error?.message || t('admin.advertising.saveError');
        } finally {
          this.isSavingAdvertising = false;
          this.render();
        }
        return;
      }
      case 'admin-users-save-allowed-countries': {
        if (this.isSavingAllowedCountries || !Array.isArray(detail.countryCodes)) {
          return;
        }

        const previousValue = [...this.allowedCountryCodes];
        const nextValue = [...new Set(
          detail.countryCodes
            .map((value: string) => String(value || '').trim().toUpperCase())
            .filter((value: string) => value.length > 0),
        )] as string[];
        this.isSavingAllowedCountries = true;
        this.allowedCountriesError = '';
        this.allowedCountryCodes = nextValue;
        this.render();

        try {
          await adminApi.setAllowedCountries({ countryCodes: nextValue });
        } catch (error: any) {
          this.allowedCountryCodes = previousValue;
          this.allowedCountriesError = error?.message || t('admin.allowedCountries.saveError');
        } finally {
          this.isSavingAllowedCountries = false;
          this.render();
        }
        return;
      }
      case 'admin-users-toggle-new-user-access': {
        if (this.isSavingNewUserAccess || typeof detail.isEnabled !== 'boolean') {
          return;
        }

        const previousValue = this.newUserAccessEnabled;
        this.isSavingNewUserAccess = true;
        this.newUserAccessError = '';
        this.newUserAccessEnabled = detail.isEnabled;
        this.render();

        try {
          await adminApi.setNewUserAccess({ isEnabled: detail.isEnabled });
        } catch (error: any) {
          this.newUserAccessEnabled = previousValue;
          this.newUserAccessError = error?.message || t('admin.newUserAccess.saveError');
        } finally {
          this.isSavingNewUserAccess = false;
          this.render();
        }
        return;
      }
      case 'admin-users-toggle-google-login': {
        if (this.isSavingGoogleLogin || typeof detail.isEnabled !== 'boolean') {
          return;
        }

        const previousValue = this.googleLoginEnabled;
        this.isSavingGoogleLogin = true;
        this.googleLoginError = '';
        this.googleLoginEnabled = detail.isEnabled;
        this.render();

        try {
          await adminApi.setGoogleLoginAccess({ isEnabled: detail.isEnabled });
        } catch (error: any) {
          this.googleLoginEnabled = previousValue;
          this.googleLoginError = error?.message || t('admin.googleLogin.saveError');
        } finally {
          this.isSavingGoogleLogin = false;
          this.render();
        }
        return;
      }
      case 'admin-users-toggle-microsoft-login': {
        if (this.isSavingMicrosoftLogin || typeof detail.isEnabled !== 'boolean') {
          return;
        }

        const previousValue = this.microsoftLoginEnabled;
        this.isSavingMicrosoftLogin = true;
        this.microsoftLoginError = '';
        this.microsoftLoginEnabled = detail.isEnabled;
        this.render();

        try {
          await adminApi.setMicrosoftLoginAccess({ isEnabled: detail.isEnabled });
        } catch (error: any) {
          this.microsoftLoginEnabled = previousValue;
          this.microsoftLoginError = error?.message || t('admin.microsoftLogin.saveError');
        } finally {
          this.isSavingMicrosoftLogin = false;
          this.render();
        }
        return;
      }
      case 'admin-users-toggle-new-subscription-access': {
        if (this.isSavingNewSubscriptionAccess || typeof detail.isEnabled !== 'boolean') {
          return;
        }

        const previousValue = this.newSubscriptionAccessEnabled;
        this.isSavingNewSubscriptionAccess = true;
        this.newSubscriptionAccessError = '';
        this.newSubscriptionAccessEnabled = detail.isEnabled;
        this.render();

        try {
          await adminApi.setNewSubscriptionAccess({ isEnabled: detail.isEnabled });
        } catch (error: any) {
          this.newSubscriptionAccessEnabled = previousValue;
          this.newSubscriptionAccessError = error?.message || t('admin.newSubscriptionAccess.saveError');
        } finally {
          this.isSavingNewSubscriptionAccess = false;
          this.render();
        }
        return;
      }
      case 'admin-users-toggle-user-selection': {
        const userId = String(detail.userId || '');
        if (!userId) {
          return;
        }

        if (detail.isSelected) {
          this.selectedUserIds.add(userId);
        } else {
          this.selectedUserIds.delete(userId);
        }

        this.render();
        return;
      }
      case 'admin-users-toggle-select-all-users': {
        const eligibleUsers = this.users.filter((user) => user.hasManageableSubscription);
        if (detail.isSelected) {
          eligibleUsers.forEach((user) => this.selectedUserIds.add(user.id));
        } else {
          eligibleUsers.forEach((user) => this.selectedUserIds.delete(user.id));
        }

        this.render();
        return;
      }
      default:
        return;
    }
  };
}

if (!customElements.get('rc-admin-users')) {
  customElements.define('rc-admin-users', RcAdminUsers);
}
